import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/apiClient.ts';
import { RefreshCw, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Balances.jsx — صفحة "أرصدة المجموعات"
 * - تحميل أولي بدون تاريخ
 * - فلترة بتاريخ من/إلى بصيغة YYYY-MM-DD
 * - جدول بخمسة أعمدة: (اسم المجموعة، سعر المجموعة، واصل نقداً، الأقساط المستلمة، الرصيد المتبقي)
 * - فوتر يحوي المجاميع
 * - يحافظ على الثيم العام (Tailwind + dark mode)
 */

const API_URL = '/v1/reports/supervisors-balances';
// داخل apiClient.ts قبل export default api;
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // 👇 أضف هذا السطر
  const tenant = localStorage.getItem("tenant") || "default-tenant-1";
  config.headers["X-Tenant"] = tenant;

  return config;
});


const Balances = () => {
  const { t } = useTranslation?.() ?? { t: (k) => k };

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // محاولة ذكية: جرّب GET أولاً، وإذا الـ API يطلب POST فقط، نحاول POST تلقائيًا.
  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (from) params.df = from;
      if (to) params.dt = to;

      let resp;
      try {
        resp = await api.get(API_URL, { params });
      } catch (e) {
        // إذا فشل الـ GET (مثلاً 405)، جرّب POST
        resp = await api.post(API_URL, { df: from || null, dt: to || null });
      }
      const data = resp?.data?.data ?? resp?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Network Error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // تحميل أولي بدون تاريخ
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const agg = { final_balance: 0 };
    for (const r of rows) {
      agg.final_balance       += Number(r?.final_balance ?? 0);
    }
    return agg;
  }, [rows]);

  return (
    <div className="w-full mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">
        {t('أرصدة المشرفين') || 'أرصدة المشرفين'}
      </h1>

      {/* فلاتر التاريخ */}
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          {/* من */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('من تاريخ') || 'مِن تاريخ'}
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
              />
            </div>
          </div>

          {/* إلى */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('إلى تاريخ') || 'إلى تاريخ'}
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
              />
            </div>
          </div>

          {/* زر تحديث */}
          <div className="flex sm:justify-end">
            <button
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium shadow-sm transition-colors duration-200"
              title={t('تحديث') || 'تحديث'}
            >
              <RefreshCw className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
              {loading ? (t('يتم الجلب...') || 'يتم الجلب...') : (t('تحديث') || 'تحديث')}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-600 dark:text-red-400">
            {error}
          </div>
        )}
      </div>

      {/* الجدول */}
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg">
        <div className="app-table-wrap">
          <table className="app-table min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  {t('اسم المشرف') || 'اسم المشرف'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  {t('الرصيد المتبقي') || 'الرصيد المتبقي'}
                </th>
              </tr>
            </thead>

            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={2} className="px-6 py-6 text-center text-gray-500 dark:text-gray-400">
                    {t('يتم الجلب...') || 'يتم الجلب...'}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-6 py-6 text-center text-gray-500 dark:text-gray-400">
                    {t('لا توجد بيانات') || 'لا توجد بيانات'}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.gr_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors duration-150">
                    <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-gray-100">
                      {r.su_name}
                    </td>
                    <td className={`px-6 py-3 whitespace-nowrap text-right text-sm font-bold ${
                      Number(r.final_balance || 0) > 0
                        ? 'text-orange-700 dark:text-orange-400'
                        : Number(r.final_balance || 0) === 0
                        ? 'text-gray-600 dark:text-gray-300'
                        : 'text-red-700 dark:text-red-400'
                    }`}>
                      {Number(r.final_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {/* فوتر المجاميع */}
            <tfoot className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <td className="px-6 py-3 text-right text-xs font-bold text-gray-700 dark:text-gray-200">
                  {t('المجاميع') || 'المجاميع'}
                </td>
                <td className={`px-6 py-3 text-right text-sm font-extrabold ${
                  totals.final_balance > 0
                    ? 'text-orange-700 dark:text-orange-400'
                    : totals.final_balance === 0
                    ? 'text-gray-600 dark:text-gray-300'
                    : 'text-red-700 dark:text-red-400'
                }`}>
                  {totals.final_balance.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Balances;
